
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Clock, TrendingUp, TrendingDown, AlertCircle } from "lucide-react";
import type { Candidate } from '../types';

interface StagePerformanceProps {
  candidates: Candidate[];
}

interface StageMetrics {
  stageName: string;
  count: number;
  avgTimeInStage: number;
  conversionRate: number;
  bottleneck: boolean;
  trend: 'up' | 'down' | 'stable';
}

export const StagePerformance: React.FC<StagePerformanceProps> = ({ candidates }) => {
  const stageMetrics: StageMetrics[] = React.useMemo(() => {
    const stages = ['new', 'screening', 'interview', 'offer', 'hired'];
    
    return stages.map((stage, index) => {
      const stageCandidates = candidates.filter(c => c.status === stage);
      const count = stageCandidates.length;
      
      // Mock calculations - in real app would use actual timing data
      const avgTimeInStage = Math.round(Math.random() * 10 + 3); // 3-13 days
      const conversionRate = index < stages.length - 1 ? 
        Math.round(Math.random() * 30 + 50) : 100; // 50-80% conversion
      
      const bottleneck = count > candidates.length * 0.3; // More than 30% in one stage
      const trend = Math.random() > 0.5 ? 'up' : Math.random() > 0.5 ? 'down' : 'stable';
      
      return {
        stageName: stage.charAt(0).toUpperCase() + stage.slice(1),
        count,
        avgTimeInStage,
        conversionRate,
        bottleneck,
        trend
      };
    });
  }, [candidates]);

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp className="h-4 w-4 text-green-500" />;
      case 'down': return <TrendingDown className="h-4 w-4 text-red-500" />;
      default: return <div className="h-4 w-4" />;
    }
  };

  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Stage Performance Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stageMetrics.map((stage) => (
            <Card key={stage.stageName} className="border-2">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{stage.stageName}</CardTitle>
                  {stage.bottleneck && (
                    <Badge variant="destructive" className="flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      Bottleneck
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span>Candidates</span>
                    <span className="font-medium">{stage.count}</span>
                  </div>
                  <Progress value={stage.count / candidates.length * 100} className="h-2" />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-xs text-muted-foreground">Avg time in stage</span>
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span className="font-medium">{stage.avgTimeInStage} days</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground">Conversion rate</span>
                    <div className="flex items-center gap-1 justify-end">
                      <span className={`font-medium ${getTrendColor(stage.trend)}`}>
                        {stage.conversionRate}%
                      </span>
                      {getTrendIcon(stage.trend)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
